import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { batchCounterApi, vendorApi } from '../services/api';
import { useUIActions } from '../store';
import { Vendor } from '../types';
import LoadingSpinner from './LoadingSpinner';

interface BatchCounter {
  vendorCode: string;
  vendorName?: string;
  counterValue: number;
}

const BatchCounterPage: React.FC = () => {
  const [counters, setCounters] = useState<BatchCounter[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCounter, setEditingCounter] = useState<string | null>(null);
  const [newCounter, setNewCounter] = useState({ vendorCode: '', counterValue: 0 });
  const [showAddForm, setShowAddForm] = useState(false);

  const { addToast } = useUIActions();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 同時獲取計數器配置和廠商列表
      const [countersRes, vendorsRes] = await Promise.all([
        batchCounterApi.getCounters(),
        vendorApi.list('active')
      ]);

      let counterList: BatchCounter[] = [];
      
      console.log('Batch counters API response:', countersRes);
      
      if (countersRes.success) {
        counterList = Object.entries(countersRes.data || {}).map(([vendorCode, counterValue]) => ({
          vendorCode,
          counterValue: counterValue as number
        }));
        console.log('Processed counter list:', counterList);
      }

      console.log('Vendors API response:', vendorsRes);
      
      if (vendorsRes.success) {
        setVendors(vendorsRes.data || []);
        
        // 為計數器添加廠商名稱
        const updatedCounters = counterList.map(counter => {
          const vendor = vendorsRes.data?.find(v => v.vendor_code === counter.vendorCode);
          return {
            ...counter,
            vendorName: vendor?.vendor_name || `廠商${counter.vendorCode}`
          };
        });
        console.log('Final updated counters:', updatedCounters);
        setCounters(updatedCounters);
      } else {
        // 如果廠商資料載入失敗，也要設定計數器資料
        console.log('Setting counters without vendor names:', counterList);
        setCounters(counterList);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      addToast({
        type: 'error',
        title: '載入失敗',
        message: '無法載入批號計數器資料'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCounter = async (vendorCode: string, counterValue: number) => {
    setSaving(true);
    try {
      const response = await batchCounterApi.setCounter(vendorCode, counterValue);
      
      if (response.success) {
        // 更新本地狀態
        setCounters(prev => {
          const existing = prev.find(c => c.vendorCode === vendorCode);
          if (existing) {
            return prev.map(c => 
              c.vendorCode === vendorCode 
                ? { ...c, counterValue }
                : c
            );
          } else {
            const vendor = vendors.find(v => v.vendor_code === vendorCode);
            return [...prev, {
              vendorCode,
              counterValue,
              vendorName: vendor?.vendor_name || `廠商${vendorCode}`
            }];
          }
        });

        setEditingCounter(null);
        setShowAddForm(false);
        setNewCounter({ vendorCode: '', counterValue: 0 });

        addToast({
          type: 'success',
          title: '設定成功',
          message: response.data?.message || `廠商 ${vendorCode} 的批號計數器已更新`
        });
      } else {
        throw new Error(response.message || '設定失敗');
      }
    } catch (error: any) {
      console.error('Save counter failed:', error);
      addToast({
        type: 'error',
        title: '設定失敗',
        message: error.message || '無法更新批號計數器'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddCounter = () => {
    if (!newCounter.vendorCode || newCounter.counterValue < 0) {
      addToast({
        type: 'error',
        title: '輸入錯誤',
        message: '請選擇廠商並輸入有效的計數器值'
      });
      return;
    }

    handleSaveCounter(newCounter.vendorCode, newCounter.counterValue);
  };

  const handleEditCounter = (vendorCode: string, newValue: number) => {
    if (newValue < 0) {
      addToast({
        type: 'error',
        title: '輸入錯誤',
        message: '計數器值不能小於0'
      });
      return;
    }

    handleSaveCounter(vendorCode, newValue);
  };

  const getNextBatchNumber = (vendorCode: string, counterValue: number) => {
    return `${vendorCode.padStart(3, '0')}000${(counterValue + 1).toString().padStart(5, '0')}`;
  };

  const availableVendors = vendors.filter(vendor => 
    !counters.some(counter => counter.vendorCode === vendor.vendor_code)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">批號計數器管理</h1>
          <p className="mt-2 text-sm text-gray-600">
            設定各廠商的批號計數器起始值，確保批號連續生成
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            新增計數器
          </button>
        </div>
      </div>

      {/* Add New Counter Form */}
      {showAddForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">新增批號計數器</h3>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewCounter({ vendorCode: '', counterValue: 0 });
              }}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                廠商
              </label>
              <select
                value={newCounter.vendorCode}
                onChange={(e) => setNewCounter(prev => ({ ...prev, vendorCode: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">請選擇廠商</option>
                {availableVendors.map(vendor => (
                  <option key={vendor.vendor_code} value={vendor.vendor_code}>
                    {vendor.vendor_name} ({vendor.vendor_code})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                計數器值
              </label>
              <input
                type="number"
                min="0"
                value={newCounter.counterValue}
                onChange={(e) => setNewCounter(prev => ({ ...prev, counterValue: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="例如: 483"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleAddCounter}
                disabled={saving || !newCounter.vendorCode}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                    儲存中...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    新增
                  </>
                )}
              </button>
            </div>
          </div>
          
          {newCounter.vendorCode && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-medium">下一個批號將是:</span>{' '}
                <span className="font-mono text-blue-900">
                  {getNextBatchNumber(newCounter.vendorCode, newCounter.counterValue)}
                </span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Counters Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            批號計數器列表 ({counters.length})
          </h3>
        </div>

        {counters.length === 0 ? (
          <div className="text-center py-12">
            <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">尚未設定任何批號計數器</p>
            <p className="text-gray-400 text-sm mt-2">點擊「新增計數器」開始設定</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    廠商資訊
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    當前計數器值
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    下一個批號
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {counters.map((counter) => (
                  <tr key={counter.vendorCode} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-primary-600 font-medium text-sm">
                            {counter.vendorCode}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {counter.vendorName || `廠商${counter.vendorCode}`}
                          </p>
                          <p className="text-xs text-gray-500">代碼: {counter.vendorCode}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCounter === counter.vendorCode ? (
                        <input
                          type="number"
                          min="0"
                          defaultValue={counter.counterValue}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const value = parseInt((e.target as HTMLInputElement).value);
                              handleEditCounter(counter.vendorCode, value);
                            } else if (e.key === 'Escape') {
                              setEditingCounter(null);
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseInt(e.target.value);
                            if (value !== counter.counterValue) {
                              handleEditCounter(counter.vendorCode, value);
                            } else {
                              setEditingCounter(null);
                            }
                          }}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          autoFocus
                        />
                      ) : (
                        <div className="flex items-center">
                          <span className="text-sm font-mono text-gray-900">
                            {counter.counterValue}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-blue-600">
                        {getNextBatchNumber(counter.vendorCode, counter.counterValue)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingCounter(counter.vendorCode)}
                          disabled={saving}
                          className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                          title="編輯計數器值"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <Cog6ToothIcon className="w-5 h-5 mr-2" />
          使用說明
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div className="space-y-2">
            <p>• 批號計數器決定該廠商下一個批號的起始數字</p>
            <p>• 例如：設定計數器為483，下一個批號將從484開始</p>
            <p>• 批號格式：{'{廠商代碼(3位)}{來源代碼(3位)}{序列號(5位)}'}</p>
          </div>
          <div className="space-y-2">
            <p>• 計數器值應設定為上一個已使用的批號數字</p>
            <p>• 系統會自動選擇計數器值和現有批號中較大的值</p>
            <p>• 修改計數器值後會立即生效，請謹慎操作</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchCounterPage;